import { getCountry, getLanguage } from 'i18n/i18nUtils';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { getQuery } from 'utils/queryUtils';
import { useTestFeature } from 'optimizely/hooks';

const chatJsId = 'chatclienjs';
const chatJsCssId = 'chatcliencss';

/**
 * Enables a help chat on planner and summary page
 *
 * You can run window.openWebchat() in console to open chat
 * You can run window.isWebchatAvailable() to check if chat has been loaded and is available
 * CTE JS https://www.cte.ikeadt.com/pt/en/one-solution-widgets/scripts/frontend-widgets.min.js?v=l0kutgf1
 * Prod JS https://www.ikea.com/pt/en/one-solution-widgets/scripts/frontend-widgets.min.js?v=l07u00jk
 * CTE CSS https://www.cte.ikeadt.com/pt/en/one-solution-widgets/style/frontend-widgets.min.css?v=l0kutgf1
 * Prod CSS https://www.ikea.com/pt/en/one-solution-widgets/style/frontend-widgets.min.css?v=l07u00jk
 */
const useChatClient = () => {
  const history = useHistory();
  const [chatLoaded, setChatLoaded] = useState(false);
  const chatClientABTestEnabled = useTestFeature('ChatClient');

  useEffect(() => {
    const chatButton = window.document.getElementsByClassName('cx-webchat-button');
    if (chatLoaded && chatButton.length > 0) {
      if (
        history.location.pathname === AppRoutes.SUMMARY ||
        history.location.pathname === AppRoutes.PLANNER
      ) {
        chatButton[0].setAttribute('style', 'display: flex; z-index: 3000');
      } else {
        chatButton[0].setAttribute('style', 'display: none; z-index: 3000');
      }
    }

    if (history.location.pathname === AppRoutes.PLANNER && !chatLoaded) {
      loadChat(chatClientABTestEnabled);
      setChatLoaded(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);
};

function loadChat(chatClientABTestEnabled: boolean) {
  const chatClientEnabled =
    getQuery().get('enableChatClient') === 'true' || chatClientABTestEnabled;
  const localeAndCountry = getLocaleAndCountry();

  if (!chatClientEnabled) return;

  loadStyles(localeAndCountry);
  loadClient(localeAndCountry);
}

function loadClient(localeAndCountry: string) {
  const chatClientLoaded = document.getElementById(chatJsId);

  if (!chatClientLoaded) {
    const script = document.createElement('script');
    script.src = 'https://www.ikea.com/{0}/one-solution-widgets/scripts/frontend-widgets.da2fabf68958df304487.min.js?6ee15bf2d601d311a4f4'.replace(
      '{0}',
      localeAndCountry
    );
    script.id = chatJsId;
    document.head.appendChild(script);
  }
}

function loadStyles(localeAndCountry: string) {
  const chatClientCssLoaded = document.getElementById(chatJsCssId);

  if (!chatClientCssLoaded) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://www.ikea.com/{0}/one-solution-widgets/style/frontend-widgets.4fa309052f97d88c6a13.min.css?6ee15bf2d601d311a4f4'.replace(
      '{0}',
      localeAndCountry
    );
    link.id = chatJsCssId;
    document.head.appendChild(link);
  }
}

function getLocaleAndCountry(): string {
  // Using encodeURIComponent() to fix a security alert
  return `${encodeURIComponent(getCountry().toLowerCase())}/${encodeURIComponent(getLanguage())}`;
}

export default useChatClient;
